import { useStore } from '@nanostores/react';
import type { ReactNode } from 'react';
import { CustomSelect } from '~/components/ui/CustomSelect';
import { BoltToken } from '~/components/ui/icons/BoltToken';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import type { PricingCardDropdownOption } from '~/lib/stores/pricing/types';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { classNames } from '~/utils/classNames';
import type { Pricing } from '~/utils/pricing';

interface PricingSelectorProps {
  label: ReactNode;
  selectedPricing: Pricing;
  onSelectPricing: (pricing: Pricing) => void;
  options: PricingCardDropdownOption[];
  disabled?: boolean;
}

export const PricingSelector = ({
  label,
  selectedPricing,
  onSelectPricing,
  options,
  disabled,
}: PricingSelectorProps) => {
  const interval = useStore(pricingStore.selectedInterval);

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-bolt-ds-textSecondary">{label}</span>
      <CustomSelect
        disabled={disabled}
        triggerNode={
          <div className="flex gap-2 items-center text-base text-bolt-ds-textPrimary">
            <BoltToken className="size-6" />
            <span>{selectedPricing.tokens} / month</span>
          </div>
        }
        changeTriggerNode={false}
        onSelect={(value) => {
          const newPricing = options.find((p) => p.pricing.level === value)?.pricing as Pricing;
          const user = userStore.getUser();
          const activeOrganization = userStore.getActiveOrganization().get();

          sendAnalyticsEvent({
            action: AnalyticsAction.Track,
            payload: {
              event: AnalyticsTrackEvent.PlanCardClicked,
              properties: {
                // user context
                user_email: user?.email,
                user_id: user?.id,

                // plan selection context
                previous_tier: selectedPricing.tier,
                selected_tier: newPricing.tier,
                previous_tokens: selectedPricing.tokens,
                selected_tokens: newPricing.tokens,
                pricing_level: newPricing.level,
                billing_interval: interval,

                // context
                active_organization_id: activeOrganization?.id,
                location: 'pricing_card_selector',
              },
            },
          });

          onSelectPricing(newPricing);
        }}
        contentClassName="normal-case max-h-61 overflow-y-auto"
        triggerClassName={classNames(
          'normal-case bg-bolt-ds-surfaceAltLight border border-bolt-elements-borderColor rounded-md h-11',
          disabled ? 'op-60 cursor-not-allowed' : '',
        )}
        items={options.map((plan) => ({
          label: (
            <div className="flex justify-between w-full">
              <div className="flex items-center gap-2">
                <span className="text-sm text-bolt-ds-textPrimary">{plan.pricing.tokens}</span>
                {plan.badge && <OptionBadge type={plan.badge} />}
              </div>

              <span className="text-sm text-bolt-ds-textTertiary">
                ${interval === 'monthly' ? plan.pricing.rateMonthly.month : plan.pricing.rateMonthly.year}
              </span>
            </div>
          ),
          value: plan.pricing.level,
        }))}
      />
    </div>
  );
};

interface OptionBadgeProps {
  type: 'current' | 'scheduled';
}

const OptionBadge = ({ type }: OptionBadgeProps) => {
  return (
    <span
      className={classNames(
        'text-xs text-bolt-ds-textPrimary font-medium capitalize',
        'h-5 flex items-center justify-center px-2 rounded-full',
        {
          'bg-bolt-ds-brand': type === 'current',
          'bg-bolt-ds-success': type === 'scheduled',
        },
      )}
    >
      {type}
    </span>
  );
};
